import { ENV } from "../util/helpers";
import {
  File,
  Folder,
  RequestDataQuery,
  MetadataItem,
  FilePart,
  MetadataCollection,
  UploadProgressEvent,
  ContainerData,
  DownloadProgressEvent,
  Field,
  FilterField,
  FilterFieldToString,
} from "./data-types";
import { UrlApiQuery } from "./util/storage-api-util";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import { emptyRequestDataProperties as emptyData } from "./data-types";
import axios, { AxiosHeaders, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { refreshToken } from "../util/refreshToken";
import { getAccessToken } from "../store/sso/src/tokenUtils";
import { message } from 'antd';

const maxFilePartSizeMB = 256;
const maxFilePartSize = maxFilePartSizeMB * 1024 * 1024;

export class StorageAPIClient {
  public static _initialize() {
    axiosRetry(axios, {
      retryCondition: (err) => {
        return axiosRetry.isRetryableError(err)
      },
      retryDelay: (retryCount, err) => {
        switch (retryCount) {
          case 1:
            return 5 * 1000;
          case 2:
            return 10 * 1000;
          case 3:
            return 15 * 1000;
          default:
            return 0;
        }
      },
      retries: 3,
      shouldResetTimeout: true,
    });
    axios.interceptors.response.use(
      function (response: AxiosResponse<any, any>) {
        return response;
      },
      async (error: any) => {
        const originalRequest = error.config;
        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          let newToken = await refreshToken();
          if(newToken !== null) {
            axios.defaults.headers.common["Authorization"] = "Bearer " + newToken;
            originalRequest.headers["Authorization"] = "Bearer " + newToken;
            return axios(originalRequest);
          }
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
  }

  public static getContainers = (context?: string): Promise<AxiosResponse> => {
    let endpointUrl = UrlApiQuery.returnContainersRequest(context);
    return axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
    });
  };

  public static getContainerInformation = (
    containerName: string
  ): Promise<AxiosResponse> => {
    const endpointUrl = `${ENV.apiUrl}/api/v1/container/${containerName}`;
    return axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
    });
  };

  public static getAllContainerContents = (
    containerName: string
  ): Promise<AxiosResponse> => {
    const endpointUrl =
      UrlApiQuery.returnContainerContentsRequest(containerName);
    return axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
    });
  };

  public static singleDataResponseForContainerContents = (
    container: ContainerData,
    continuationToken: string,
    previousToken: string,
    limit: number,
    folder?: string
  ): Promise<RequestDataQuery> => {
    const endpointUrl = UrlApiQuery.returnContainerContentsRequest(
      container.containerName,
      folder,
      continuationToken,
      limit
    );

    const dataQuerry: RequestDataQuery = new RequestDataQuery(
      emptyData.EMPTY_FOLDERS,
      emptyData.EMPTY_FILES,
      emptyData.EMPTY_PATH,
      container.containerName,
      emptyData.EMPTY_PARENT_FOLDER,
      emptyData.EMPTY_CURRENT_TOKEN,
      emptyData.EMPTY_NEXT_TOKEN,
      emptyData.EMPTY_PREVIOUS_TOKEN,
      limit,
      false
    );
    const response = axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
    });
    return response
      .then((response) => response.data)
      .then((json) => {
        if (json.succeeded === true) {
          dataQuerry.files = json.files
            .filter(
              (file: any) => file.fileName && typeof file.fileName === "string"
            )
            .map((file: any) => {
              const dict: { [key: string]: string } = {};
              Object.keys(file.metadata).map((key: string) => {
                dict[key] = file.metadata[key];
              });
              return new File(
                file.fileName,
                file.fileSize,
                new MetadataCollection(container.metadataTemplate, dict),
                file.fileGuid,
                file.folderName,
                file.modifiedTime
              );
            });
          dataQuerry.folders = json.folders
            .filter(
              (folder: any) =>
                folder.folderName && typeof folder.folderName === "string"
            )
            .map((folder: any) => new Folder(folder.folderName));
          if (json.containerName) {
            dataQuerry.containerName = json.containerName;
          }
          if (json.parentFolder) {
            dataQuerry.parentFolder = json.parentFolder;
          }
          if (json.currentContinuationToken) {
            dataQuerry.currentContinuationToken = json.currentContinuationToken;
          }
          if (json.nextContinuationToken) {
            dataQuerry.nextContinuationToken = json.nextContinuationToken;
          }
          if (json.moreDataAvailable) {
            dataQuerry.moreDataAvaiable = json.moreDataAvailable;
          }
          dataQuerry.path =
            container.containerName + "/" + dataQuerry.parentFolder;
          dataQuerry.previousContinuationToken = previousToken;
          return dataQuerry;
        } else {
          console.error("Error:", json.errorMessage);
          return dataQuerry;
        }
      })
      .catch((error) => {
        console.error("Error:", error.message);
        return dataQuerry;
      });
  };

  public static searchByVirtualFolderPattern = (
    container: ContainerData,
    offset: number,
    prevOffset: number,
    limit: number,
    folder?: string,
    fileName?: string,
    sortField?: Field,
    ascending?: boolean,
    filterFields?: FilterField[]
  ): Promise<RequestDataQuery> => {
    const endpointUrl = UrlApiQuery.returnFolderSearchQuery(
      container.containerName,
      offset,
      limit,
      folder,
      fileName,
      sortField,
      ascending
    );
    return this.getFilesFromSearchRequest(
      container,
      endpointUrl,
      offset,
      prevOffset,
      limit,
      "folder",
      filterFields
    );
  };

  public static searchByMetadata = (
    container: ContainerData,
    metaDataKey: string,
    metaDataValue: string,
    offset: number,
    prevOffset: number,
    limit: number,
    folder?: string,
    sortField?: Field,
    ascending?: boolean,
    filterFields?: FilterField[]
  ): Promise<RequestDataQuery> => {
    const endpointUrl = UrlApiQuery.returnMetadataSearchQuery(
      container.containerName,
      metaDataKey,
      metaDataValue,
      offset,
      limit,
      folder,
      sortField,
      ascending
    );
    return this.getFilesFromSearchRequest(
      container,
      endpointUrl,
      offset,
      prevOffset,
      limit,
      "metadata",
      filterFields
    );
  };

  public static searchByGdriveLink = (
    container: ContainerData,
    GDriveLink: string,
    offset: number,
    prevOffset: number,
    limit: number
  ): Promise<RequestDataQuery> => {
    let template: RegExp =
      /https:\/\/gdrive(?:|-dev|-test).gsk.com\/gdrive\/drive\/(?:ECS|Azure)\/(?<gdrivebucket>[^?]+)\?(?:downloadFile=|viewFile=)(?<gdrivepath>[A-Za-z0-9+/=]+)/i;
    const parts = template.exec(GDriveLink);
    let GDriveBucket: string = "";
    let OldFilePath: string = "";

    if (parts && parts.groups) {
      GDriveBucket = parts.groups.gdrivebucket;
      OldFilePath = atob(parts.groups!.gdrivepath);
    }

    const endpointUrl = UrlApiQuery.returnGdriveSearchQuery(
      container.containerName,
      GDriveBucket,
      OldFilePath,
      offset,
      limit
    );

    return this.getFilesFromSearchRequest(
      container,
      endpointUrl,
      offset,
      prevOffset,
      limit,
      "gdrive"
    );
  };

  public static downloadFile = async (
    containerName: string,
    file: File,
    fileGUID: string,
    onProgress: (ev: DownloadProgressEvent) => void,
    writableStream: FileSystemWritableFileStream,
    folderName?: string
  ): Promise<void> => {
    if (file.fileSize < maxFilePartSize) {
      return this.downloadFileOneRequest(
        containerName,
        file,
        fileGUID,
        onProgress,
        writableStream,
        folderName
      );
    } else {
      return this.downloadFileInParts(
        containerName,
        file,
        fileGUID,
        onProgress,
        writableStream,
        folderName
      );
    }
  };

  public static downloadFileOneRequest = async (
    containerName: string,
    file: File,
    fileGUID: string,
    onProgress: (ev: DownloadProgressEvent) => void,
    writableStream: FileSystemWritableFileStream,
    folderName?: string
  ): Promise<void> => {
    const endpointUrl = UrlApiQuery.returnDownloadFileRequest(
      containerName,
      file.name,
      fileGUID,
      folderName
    );
    const response = axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
      responseType: "blob",
      onDownloadProgress(progressEvent) {
        onProgress(
          new DownloadProgressEvent(
            file.fileSize,
            progressEvent.loaded,
            progressEvent.rate
          )
        );
      },
    });
    return response
      .then((response) => response.data)
      .then((blob) => {
        writableStream.write(blob);
        writableStream.close();
      })
      .catch((error) => {
        console.error("Error:", error.message);
      });
  };

  public static downloadFileInParts = async (
    containerName: string,
    file: File,
    fileGUID: string,
    onProgress: (ev: DownloadProgressEvent) => void,
    writableStream: FileSystemWritableFileStream,
    folderName?: string
  ): Promise<void> => {
    const chunks: number = Math.ceil(file.fileSize! / maxFilePartSize);
    const chunkSize = maxFilePartSize;
    for (let i = 0; i < chunks; i++) {
      const startByte: number = i * chunkSize;
      const partSize = i < chunks - 1 ? chunkSize : file.fileSize - startByte;
      let blob = await this.downloadFileOnePart(
        containerName,
        file,
        fileGUID,
        startByte,
        partSize,
        onProgress,
        folderName
      );
      await writableStream.write(blob);
    }     
    await writableStream.close();
  };

  public static downloadFileOnePart = (
    containerName: string,
    file: File,
    fileGUID: string,
    startAt: number,
    totalBytes: number,
    onProgress: (ev: DownloadProgressEvent) => void,
    folderName?: string
  ): Promise<Blob> => {
    let endpointUrl = UrlApiQuery.returnDownloadOnePartQuery(
      containerName,
      file.name,
      fileGUID,
      startAt,
      totalBytes,
      folderName
    );
    const response = axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
      responseType: "blob",
      onDownloadProgress(progressEvent) {
        onProgress(
          new DownloadProgressEvent(
            file.fileSize,
            startAt + progressEvent.loaded,
            progressEvent.rate
          )
        );
      },
    });
    return response
      .then((response) => response.data)
      .catch((error) => {
        console.error("Error:", error.message);
      });
  };

  public static uploadFileWithMetadataFile = (
    containerName: string,
    file: UploadFile,
    metadata: MetadataItem[],
    onProgress: (ev: UploadProgressEvent) => void,
    folder?: string,
    fileName?: string
  ): Promise<AxiosResponse> => {
    if (file.size! <= maxFilePartSize) {
      return this.uploadFileOneRequest(
        containerName,
        file,
        metadata,
        onProgress,
        folder,
        fileName
      );
    } else {
      return this.uploadFileInParts(
        containerName,
        file,
        metadata,
        onProgress,
        folder,
        fileName
      );
    }
  };

  public static uploadFileOneRequest = (
    containerName: string,
    file: UploadFile,
    metadata: MetadataItem[],
    onProgress: (ev: UploadProgressEvent) => void,
    folder?: string,
    fileName?: string
  ): Promise<AxiosResponse> => {
    let endpointUrl = UrlApiQuery.returnUploadFileOneQuery(
      containerName,
      folder,
      fileName
    );
    let rcfile = file as RcFile;
    let formData = new FormData();
    formData.append("File", rcfile);

    for (let j = 0; j < metadata.length; j++) {
      formData.append("Metadata.Items", JSON.stringify(metadata[j]));
    }

    let response = axios(endpointUrl, {
      headers: this.getHeaders("application/json", "multipart/form-data"),
      method: "POST",
      data: formData,
      onUploadProgress(progressEvent) {
        onProgress(
          new UploadProgressEvent(
            file.name,
            file.size!,
            0,
            progressEvent.loaded,
            progressEvent.rate
          )
        );
      },
    });

    formData = new FormData();
    return response;
  };

  public static uploadSessionInit = (
    containerName: string,
    file: UploadFile,
    folder?: string,
    fileName?: string
  ): Promise<string> => {
    let initUrl = UrlApiQuery.returnUploadPartsInitQuery(
      containerName,
      fileName ? fileName : file.name,
      folder
    );
    const initResponse = axios(initUrl, {
      headers: this.getHeaders("application/json"),
      method: "GET",
    });

    return initResponse
      .then((response) => response.data)
      .then((json) => json.uploadSessionId)
      .catch(() => "error");
  };

  public static uploadOnePart = (
    containerName: string,
    file: UploadFile,
    fileSize: number,
    filePartSize: number,
    filePartNumber: number,
    filePart: Blob,
    UploadSessionId: string,
    onProgress: (ev: UploadProgressEvent) => void,
    folder?: string,
    fileName?: string
  ): Promise<FilePart> => {
    let endpointUrl = UrlApiQuery.returnUploadPartQuery(
      containerName,
      fileName ? fileName : file.name,
      fileSize,
      filePartSize,
      filePartNumber,
      folder
    );

    let formData = new FormData();
    formData.append("FilePart", filePart, file.name);
    formData.append("UploadSessionId", UploadSessionId);

    const response = axios(endpointUrl, {
      headers: this.getHeaders(
        "application/json; charset=utf8",
        "multipart/form-data"
      ),
      method: "POST",
      data: formData,
      onUploadProgress(progressEvent) {
        onProgress(
          new UploadProgressEvent(
            fileName ? fileName : file.name,
            fileSize,
            (filePartNumber - 1) * filePartSize,
            progressEvent.loaded,
            progressEvent.rate
          )
        );
      },
    });

    return response
      .then((response) => response.data)
      .then((json) => {
        return new FilePart(json.filePartNumber, json.partGuid);
      })
      .catch(() => {
        return new FilePart(0, "");
      });
  };

  public static uploadCommit = (
    containerName: string,
    file: UploadFile,
    metadata: MetadataItem[],
    uploadSessionId: string,
    fileParts: FilePart[],
    folder?: string,
    fileName?: string
  ): Promise<AxiosResponse> => {
    let endpointUrl = UrlApiQuery.returnUploadPartsCommitQuery(
      containerName,
      fileName ? fileName : file.name,
      folder
    );

    let requestObject = {
      uploadSessionId: uploadSessionId,
      metadata: {
        items: metadata,
      },
      parts: {
        items: fileParts,
      },
    };

    const response = axios(endpointUrl, {
      headers: this.getHeaders("application/json; charset=utf8"),
      method: "POST",
      data: requestObject,
      timeout: 3600 * 1000, //wait for an hour
    });

    return response;
  };

  public static uploadFileInParts = async (
    containerName: string,
    file: UploadFile,
    metadata: MetadataItem[],
    onProgress: (ev: UploadProgressEvent) => void,
    folder?: string,
    fileName?: string
  ): Promise<AxiosResponse> => {
    const uploadSessionID = await this.uploadSessionInit(
      containerName,
      file,
      folder,
      fileName
    );

    let fileParts: FilePart[] = [];
    const chunks: number = Math.ceil(file.size! / maxFilePartSize);
    const chunkSize = Math.ceil(file.size! / chunks);
    let rcFile = file as RcFile;
    for (let i = 0; i < chunks; i++) {
      const startByte: number = i * chunkSize;
      const endByte: number = Math.min((i + 1) * chunkSize, file.size!);
      let filePart: Blob = rcFile.slice(startByte, endByte);
      let filePartInfo: FilePart = await this.uploadOnePart(
        containerName,
        file,
        file.size!,
        filePart.size,
        i + 1,
        filePart,
        uploadSessionID,
        onProgress,
        folder,
        fileName
      );
      if (filePartInfo.partGuid) {
        fileParts = [...fileParts, filePartInfo];
      }
      else {
        const errorResponse: AxiosResponse = {
          data: {
            message: `File part ${i+1} failed to upload. Upload of ${file.name} was cancelled`,
            status: "Error"
          },
          status: 500,
          headers: {},
          config: {
            method: "POST",
            headers: new AxiosHeaders()
          },
          statusText: ""
        }
        console.error(errorResponse.data.message);
        return errorResponse;
      }
    }

    return this.uploadCommit(
      containerName,
      file,
      metadata,
      uploadSessionID,
      fileParts,
      folder,
      fileName
    );
  };

  public static getFoldersFromContainer = async (
    containerName: string,
    offset: number,
    prevOffset: number,
    limit: number,
    parentFolder?: string,
    folderPattern?: string
  ): Promise<RequestDataQuery> => {
    const endpointUrl = UrlApiQuery.returnGetFoldersQuery(
      containerName,
      offset,
      limit,
      parentFolder,
      folderPattern
    );

    const dataQuerry: RequestDataQuery = new RequestDataQuery(
      emptyData.EMPTY_FOLDERS,
      emptyData.EMPTY_FILES,
      emptyData.EMPTY_PATH,
      containerName,
      emptyData.EMPTY_PARENT_FOLDER,
      emptyData.EMPTY_CURRENT_TOKEN,
      emptyData.EMPTY_NEXT_TOKEN,
      emptyData.EMPTY_PREVIOUS_TOKEN,
      limit,
      false
    );

    if (parentFolder) {
      dataQuerry.parentFolder = parentFolder;
    }

    return axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
    })
      .then((response) => response.data)
      .then((json) => {
        if (json.succeeded) {
          dataQuerry.folders = json.data
            .filter((folder: any) => typeof folder === "string")
            .map((folder: any) => new Folder(folder));
          if (json.offset) {
            dataQuerry.currentContinuationToken = String(json.offset);
          }
          if (json.nextOffset) {
            dataQuerry.nextContinuationToken = String(json.nextOffset);
          }
          if (json.moreDataAvailable) {
            dataQuerry.moreDataAvaiable = json.moreDataAvailable;
          }
        }
        return dataQuerry;
      })
      .catch((error) => {
        console.error(error.message);
        return dataQuerry;
      });
  };

  public static getKeysFromContainer = async (
    containerName: string
  ): Promise<string[]> => {
    const endpointUrl = UrlApiQuery.returnKeysFromContainerQuery(containerName);

    let metadataKeys: string[] = [];

    return axios(endpointUrl, {
      headers: this.getHeaders(),
      method: "GET",
    })
      .then((response) => response.data)
      .then((json) => {
        if (json.succeeded) {
          metadataKeys = json.data.filter(
            (metadataKey: any) => typeof metadataKey === "string"
          );
        }
        return metadataKeys;
      })
      .catch((error) => {
        console.error(error.message);
        return metadataKeys;
      });
  };

  public static updateMetadata = (
    containerName: string,
    file: File,
    metadata: MetadataItem[]
  ): Promise<AxiosResponse> => {
    let endpointUrl = UrlApiQuery.returnMetadataUpdateQuery(
      containerName,
      file
    );

    let formData = new FormData();
    let response: Promise<AxiosResponse>;

    for (let i = 0; i < metadata.length; i++) {
      formData.append("Items", JSON.stringify(metadata[i]));
    }

    response = axios(endpointUrl, {
      headers: this.getHeaders("application/json", "multipart/form-data"),
      method: "POST",
      data: formData,
    });

    return response;
  };

  public static massUpdateMetadata = (
    containerName: string,
    files: File[],
    metadata: MetadataItem[]
  ): Promise<AxiosResponse> => {
    let endpointUrl = UrlApiQuery.returnMassMetadataUpdateQuery(containerName);

    let formData = new FormData();
    let response: Promise<AxiosResponse>;

    for (let i = 0; i < files.length; i++) {
      formData.append("File", files[i].folderName + "/" + files[i].name);
    }

    for (let i = 0; i < metadata.length; i++) {
      formData.append("Metadata.Items", JSON.stringify(metadata[i]));
    }

    response = axios(endpointUrl, {
      headers: this.getHeaders("application/json", "multipart/form-data"),
      method: "POST",
      data: formData,
    });

    return response;
  };

  private static getHeaders = (
    accept = "text/plain",
    contentType = "application/json"
  ) => {
    const token = "Bearer " + getAccessToken();
    return {
      "Content-Type": contentType,
      accept: accept,
      apikey: ENV.apiKey,
      Authorization: token,
    };
  };

  private static getFilesFromSearchRequest = (
    container: ContainerData,
    endpointUrl: string,
    currentOffset: number,
    previousOffset: number,
    limit: number,
    searchType: string,
    filterFields?: FilterField[]
  ): Promise<RequestDataQuery> => {
    const dataQuerry: RequestDataQuery = new RequestDataQuery(
      emptyData.EMPTY_FOLDERS,
      emptyData.EMPTY_FILES,
      emptyData.EMPTY_PATH,
      container.containerName,
      emptyData.EMPTY_PARENT_FOLDER,
      emptyData.EMPTY_OFFSET,
      emptyData.EMPTY_OFFSET,
      emptyData.EMPTY_OFFSET,
      limit,
      false
    );
    let response: Promise<AxiosResponse>;
    if (searchType !== "gdrive") {
      let formData = new FormData();
      if (filterFields) {
        for (let i = 0; i < filterFields.length; i++) {
          formData.append(
            "filterField",
            JSON.stringify(new FilterFieldToString(filterFields[i]))
          );
        }
      }
      response = axios(endpointUrl, {
        headers: this.getHeaders("application/json", "multipart/form-data"),
        method: "POST",
        data: formData,
      });
    } else {
      response = axios(endpointUrl, {
        headers: this.getHeaders(),
        method: "GET",
      });
    }
    return response
      .then((response) => response.data)
      .then((json) => {
        if (json.succeeded) {
          dataQuerry.files = json.data
            .filter(
              (file: any) => file.fileName && typeof file.fileName === "string"
            )
            .map((file: any) => {
              const dict: { [key: string]: string } = {};
              if (file.metadata) {
                file.metadata.map((item: any) => (dict[item.key] = item.value));
              }
              return new File(
                file.fileName,
                file.fileSize,
                new MetadataCollection(container.metadataTemplate, dict),
                file.fileGuid,
                file.folderName,
                file.modificationDate
              );
            });
          if (json.containerName) {
            dataQuerry.containerName = json.containerName;
          }
          if (json.parentFolder) {
            dataQuerry.parentFolder = json.parentFolder;
          }
          if (json.offset) {
            dataQuerry.currentContinuationToken = String(json.offset);
          }
          if (json.nextOffset) {
            dataQuerry.nextContinuationToken = String(json.nextOffset);
          }
          if (json.moreDataAvailable) {
            dataQuerry.moreDataAvaiable = json.moreDataAvailable;
          }
          dataQuerry.path =
            container.containerName + "/" + dataQuerry.parentFolder;
          dataQuerry.previousContinuationToken = String(previousOffset);
          return dataQuerry;
        } else {
          console.error("Error:", json.errorMessage);
          return dataQuerry;
        }
      })
      .catch((error) => {
        console.error("Error:", error.message);
        return dataQuerry;
      });
  };
}
StorageAPIClient._initialize();
